import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@lib/supabase/client";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Check, X, Monitor, Users, Package, BarChart3, MessageSquare, CreditCard, ArrowLeft, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@shared/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@shared/components/ui/accordion";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { toast } from "sonner";
import MainHeader from "@shared/components/layout/MainHeader";
import Footer from "@shared/components/layout/Footer";
import WhatsAppWidget from "@shared/components/common/WhatsAppWidget";
import { AnimatedSection, AnimatedText, AnimatedCard } from "@shared/components/common/AnimatedSection";
import { useAuth } from "@app/providers/AuthProvider";

interface ClinicSubscriptionProduct {
  id: string;
  name: string;
  name_ar: string;
  description: string | null;
  description_ar: string | null;
  price: number;
  monthly_price: number | null;
  yearly_price: number | null;
  product_key: string;
}

// Map product_key to plan features
// Base features for each tier (only unique features per tier)
const commonFeatures = [
  'صفحة موقع إلكتروني خاص بالعيادة',
  'شعار العيادة وهوية بصرية مخصصة',
  'مكالمات صوتية أو مرئية مدمجة داخل العيادة',
  'محادثات نصية مباشرة داخل العيادة',
  'إمكانية إرفاق ملفات ومستندات',
  'مدفوعات إلكترونية تدعم مدى وفيزا وApple Pay عبر بوابة الدفع الرسمية (مُيسر)',
  'بوابة خاصة بالمراجعين في موقع العيادة',
  'استضافة سحابية آمنة',
  'بنية تقنية متوافقة مع متطلبات نظام حماية البيانات الشخصية (PDPL)',
  'إضافة عدد لا محدود من الخدمات والمنتجات',
  'عدد مراجعين غير محدود',
  'ملف مراجع رقمي لكل مريض',
  'نظام حجز مواعيد ذكي',
  'تنبيهات وإشعارات تلقائية للمراجع والمختص',
  'لوحة تحكم وتقارير تشغيلية',
  'دعم المحتوى باللغتين العربية والإنجليزية',
  'دومين خاص للعيادة',
];

const differentiatorFeatures: { name: string; minTier: number; excludedLabel?: string; baseLabel?: string }[] = [
  { name: 'بدون عمولة على المبيعات', minTier: 1, baseLabel: 'عمولة 20% على المبيعات' },
  { name: 'إمكانية إضافة استشارة مجانية', minTier: 1 },
  { name: 'تقارير متقدمة وتحليلات شاملة', minTier: 1 },
  { name: 'دعم فني بأولوية', minTier: 1, baseLabel: 'دعم فني' },
  { name: 'برنامج مصمم الوجبات (مشمول)', minTier: 2 },
  { name: 'إدارة واجهة الموقع (بانرات إعلانية وتصنيفات)', minTier: 2 },
  { name: 'مدير حساب مخصص', minTier: 3 },
  { name: 'تخصيص كامل للتصميم', minTier: 3 },
  { name: 'دعم فني على مدار الساعة', minTier: 3 },
  { name: 'صلاحية استخدام العيادة لأكثر من ممارس', minTier: 3 },
];

const PLAN_TIER: Record<string, number> = {
  'clinic-subscription-beginner': 0,
  'clinic-subscription-professional': 1,
  'clinic-subscription-advanced': 2,
  'clinic-subscription-enterprise': 3,
};

const PLAN_SUBTITLES: Record<string, string> = {
  'clinic-subscription-beginner': 'أول خطوة لبناء عيادتك الإلكترونية',
  'clinic-subscription-professional': 'لتجربة متكاملة بلا حدود',
  'clinic-subscription-advanced': 'أعلى مستوى من التميز والاحتراف',
  'clinic-subscription-enterprise': 'حل متكامل للمنشآت الصحية الكبيرة',
};

const getPlanFeatures = (productKey: string): { common: string[]; differentiators: { name: string; included: boolean }[]; isEnterprise?: boolean; isFree?: boolean; subtitle?: string } => {
  const tier = PLAN_TIER[productKey] ?? 0;
  return {
    common: commonFeatures,
    differentiators: differentiatorFeatures.map((d) => {
      const meetsTier = tier >= d.minTier;
      if (d.baseLabel && !meetsTier) {
        return { name: d.baseLabel, included: true };
      }
      return { name: meetsTier ? d.name : (d.excludedLabel ?? d.name), included: meetsTier };
    }),
    isEnterprise: productKey === 'clinic-subscription-enterprise',
    isFree: productKey === 'clinic-subscription-beginner',
    subtitle: PLAN_SUBTITLES[productKey],
  };
};

// Note: Plan names are now "ممارس محترف" / "ممارس متقدم" / "ممارس منشآت" في الباقات

const startClinicFaqs = [
  {
    question: "لماذا DietiTech؟",
    answer: "لأننا نوفر لك جميع احتياجاتك لبدء نشاطك المهني وممارسة عملك إلكترونياً عبر خدمة العيادة الإلكترونية + أدوات تقنية تساعدك في أداء عملك، جميعها في مكان واحد.",
  },
  {
    question: "مميزات العيادة الإلكترونية من DietiTech؟",
    answer: "تمكنك من امتلاك عيادة إلكترونية خاصة بك وبهويتك الخاصة، وتوفر لك موقع إلكتروني لعرض باقاتك ومنتجاتك، نظام حجز مواعيد متكامل، مدفوعات إلكترونية عبر بوابة دفع محلية، محادثات شات ومكالمات صوتية أو مرئية مدمجة، بوابة للمراجع، تصميم قوالب خاصة، ولوحة تحكم متكاملة لإدارة العيادة باحترافية.",
  },
  {
    question: "كيفية استلام إيرادات العيادة؟",
    answer: "يتم تحويل المدفوعات أسبوعياً يومي (الإثنين والخميس) إلى حسابك البنكي، حيث أن الحد الأدنى للتحويل هو ١٠٠ ريال.",
  },
  {
    question: "هل يوجد رسوم عمولة على المبيعات؟",
    answer: "يعتمد على الباقة:\n\nفي الباقة المجانية: يوجد عمولة ٢٠٪ على المبيعات.\n\nفي الباقات المدفوعة: لا يوجد عمولة إطلاقاً، جميع مبيعات العيادة يتم تسويتها لحسابك البنكي (مع خصم رسوم بوابة الدفع وهي ١٪+١ ريال للمدفوعات عبر مدى، أو ٢.٧٥٪+١ ريال للمدفوعات عبر بطاقة الائتمان فيزا وماستر كارد).",
  },
  {
    question: "هل يتم توفير العملاء من قبل DietiTech؟",
    answer: "DietiTech توفر للممارس الصحي العيادة الإلكترونية والبنية التقنية اللازمة لإدارة ممارسته الرقمية باحترافية، بينما يتم استقطاب العملاء من خلال الأخصائي نفسه، سواء عبر التسويق بالمحتوى في مواقع التواصل الاجتماعي الخاصة به أو من خلال مراجعيه في عيادته الحضورية الراغبين بالمتابعة عن بُعد.",
  },
  {
    question: "ماهي المتطلبات لفتح العيادة الإلكترونية وكيف طريقة التسجيل؟",
    answer: "المتطلبات هي تصنيف ساري من هيئة التخصصات الصحية مناسب لطبيعة النشاط والتخصص، وطريقة التسجيل هي عن طريق اختيار الباقة المناسبة ومن ثم تعبئة نموذج التسجيل كامل.",
  },
];


const StartClinic = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [products, setProducts] = useState<ClinicSubscriptionProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState<{ id: string; approval_status: string; subscription_status: string } | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [showEnterpriseDialog, setShowEnterpriseDialog] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({ orgName: '', orgType: '', employeeCount: '', phone: '', email: '', message: '' });
  const [submittingEnterprise, setSubmittingEnterprise] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;

    const checkAuthAndFetchData = async () => {
      let adminStatus = false;

      if (user) {
        // Check if admin
        const { data: isAdminData } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });
        adminStatus = isAdminData || false;
        setIsAdmin(adminStatus);

        // Check if user already has a clinic
        const { data: clinicData } = await supabase
          .from('clinics')
          .select('id, approval_status, subscription_status')
          .eq('specialist_id', user.id)
          .maybeSingle();

        if (clinicData) {
          setClinic(clinicData);
        } else {
          const pendingPlan = localStorage.getItem('pendingClinicPlan');
          if (pendingPlan) {
            localStorage.removeItem('pendingClinicPlan');
            navigate(`/clinic/register?plan=${pendingPlan}`);
            setLoading(false);
            return;
          }
        }
      }

      // Fetch clinic subscription products from products table
      const { data: productsData } = await supabase
        .from('products')
        .select('id, name, name_ar, description, description_ar, price, monthly_price, yearly_price, product_key')
        .like('product_key', 'clinic-subscription%')
        .eq('is_active', true)
        .order('price', { ascending: true });

      if (productsData) {
        setProducts(productsData);
      }

      setLoading(false);
    };

    checkAuthAndFetchData();
  }, [user?.id, authLoading]);

  const handleAuthAction = async () => {
    if (user) {
      await supabase.auth.signOut();
      navigate("/");
    } else {
      localStorage.setItem('authReturnTo', '/clinic/start');
      navigate("/auth?returnTo=%2Fclinic%2Fstart");
    }
  };

  const handleStartClinic = (planId: string) => {
    if (!user) {
      // Save intended plan and redirect to auth
      localStorage.setItem('pendingClinicPlan', planId);
      localStorage.setItem('authReturnTo', '/clinic/start');
      navigate('/auth?returnTo=%2Fclinic%2Fstart');
      return;
    }

    if (clinic) {
      if (clinic.approval_status === 'approved' && clinic.subscription_status === 'active') {
        navigate('/clinic/dashboard');
      } else {
        navigate('/clinic/pending');
      }
      return;
    }

    navigate(`/clinic/register?plan=${planId}`);
  };

  const features = [
    {
      icon: Monitor,
      title: "عيادة إلكترونية خاصة",
      description: "موقع متكامل بهويتك الخاصة ورابط مميز"
    },
    {
      icon: Package,
      title: "عرض الباقات والمنتجات",
      description: "إنشاء باقات متنوعة مدفوعة ومجانية"
    },
    {
      icon: Users,
      title: "إدارة العملاء",
      description: "ملفات شاملة وتتبع للتقدم والقياسات، وجدولة المواعيد"
    },
    {
      icon: MessageSquare,
      title: "التواصل المباشر",
      description: "محادثات ومكالمات صوتية ومرئية"
    },
    {
      icon: CreditCard,
      title: "بوابات الدفع",
      description: "استلام المدفوعات بشكل آمن"
    },
    {
      icon: BarChart3,
      title: "التقارير والإحصائيات",
      description: "متابعة الأداء والمبيعات"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <MainHeader user={user} isAdmin={isAdmin} onAuthAction={handleAuthAction} transparent />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col relative overflow-hidden mx-2 sm:mx-3 mt-2 sm:mt-3 rounded-3xl">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            src="https://wojdhvrrraxqpcdmeaqb.supabase.co/storage/v1/object/public/product-videos/clinic-bg.mp4.mp4"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        {/* Hero Content */}
        <div className="flex-1 flex flex-col items-center justify-center container mx-auto px-4 text-center pt-28 pb-20 relative z-10">
          <AnimatedText delay={0}>
            <h1 className="text-[2.5rem] md:text-[3.25rem] lg:text-[4rem] mb-6 leading-tight tracking-tight" style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}>
              <span className="text-white block mb-2">المنصة المتكاملة</span>
              <span className="text-white/90">للعيادات الإلكترونية</span>
            </h1>
          </AnimatedText>

          <AnimatedText delay={0.1}>
            <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-8 leading-relaxed">
              خدمة متكاملة للممارسين الصحيين. أنشئ عيادتك الخاصة، اعرض باقاتك،
              <br className="hidden md:block" />
              واستقبل عملاءك كل ذلك في مكان واحد.
            </p>
          </AnimatedText>

          <AnimatedSection delay={0.2}>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {clinic ? (
                <Button
                  size="default"
                  onClick={() => {
                    if (clinic.approval_status === 'approved' && clinic.subscription_status === 'active') {
                      navigate('/clinic/dashboard');
                    } else {
                      navigate('/clinic/pending');
                    }
                  }}
                  className="gap-2 px-6 py-4 text-base"
                >
                  الذهاب إلى عيادتي الإلكترونية
                  <ArrowLeft size={18} />
                </Button>
              ) : (
                <Button
                  size="default"
                  onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
                  className="gap-2 px-6 py-4 text-base"
                >
                  <ArrowDown size={18} />
                  أنشئ عيادتك الآن
                </Button>
              )}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              <div className="text-center">
                <div className="text-xl md:text-2xl font-bold text-white mb-1">٤</div>
                <div className="text-xs text-white/70">باقات متنوعة</div>
              </div>
              <div className="text-center border-x border-white/30 px-6 md:px-12">
                <div className="text-xl md:text-2xl font-bold text-white mb-1">٢٤/٧</div>
                <div className="text-xs text-white/70">دعم فني</div>
              </div>
              <div className="text-center">
                <div className="text-xl md:text-2xl font-bold text-white mb-1">١٠٠٪</div>
                <div className="text-xs text-white/70">خصوصية وأمان</div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
          <div className="animate-bounce">
            <button
              onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
              className="p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
            >
              <ArrowDown className="text-white" size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <AnimatedText delay={0} className="text-3xl font-bold text-center mb-12">
            ماذا تقدم لك خدمة العيادة الإلكترونية؟
          </AnimatedText>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <AnimatedCard key={index} index={index} delay={0.1}>
                <Card className="border border-border/60 shadow-lg hover:shadow-xl transition-shadow bg-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 rounded-xl bg-white border border-border/50 flex items-center justify-center mb-4 shadow-lg shadow-primary/10">
                      <feature.icon className="h-7 w-7 text-primary" strokeWidth={2} />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section id="plans" className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <AnimatedText className="text-3xl font-bold text-center mb-4">
            اختر الباقة المناسبة لك
          </AnimatedText>
          
          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-12">
            <div className="relative inline-flex items-center">
              <div className="inline-flex items-center bg-muted rounded-full p-1 gap-0">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billingPeriod === 'monthly'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  شهري
                </button>
                <button
                  onClick={() => setBillingPeriod('yearly')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billingPeriod === 'yearly'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  سنوي
                </button>
              </div>
              <span className={`absolute -left-20 top-1/2 -translate-y-1/2 text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-3 py-1 rounded-full whitespace-nowrap transition-opacity ${
                billingPeriod === 'yearly' ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}>
                وفّر أكثر
              </span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => {
              const planDetails = getPlanFeatures(product.product_key);
              const isEnterprise = planDetails.isEnterprise;
              const isFree = planDetails.isFree;
              const isPopular = product.product_key === 'clinic-subscription-professional';
              const isPremium = product.product_key === 'clinic-subscription-advanced';
              return (
                <AnimatedCard key={product.id} index={index} delay={0.2} className="h-full">
                  <Card 
                    className="relative overflow-hidden transition-all h-full flex flex-col border-border"
                  >
                    {isPopular && (
                      <div className="absolute -left-8 top-6 rotate-[-45deg] bg-primary text-primary-foreground text-center py-1 text-xs font-medium w-32">
                        الأكثر طلباً
                      </div>
                    )}
                    {isPremium && (
                      <div className="absolute -left-8 top-6 rotate-[-45deg] bg-amber-500 text-white text-center py-1 text-xs font-medium w-32">
                        الأكثر تميّزاً
                      </div>
                    )}
                    
                    {/* Plan Header */}
                    <CardHeader className="text-center pb-4 min-h-[200px] flex flex-col justify-between">
                      <CardTitle className="text-xl font-bold">{product.name_ar}</CardTitle>
                      
                      {/* Price */}
                       <div className="mt-5 min-h-[80px] flex flex-col items-center justify-center">
                        {isEnterprise ? (
                          <span className="text-lg font-bold text-primary">سعر مخصص</span>
                        ) : isFree ? (
                          <div>
                            <span className="text-4xl font-bold">مجاناً</span>
                          </div>
                        ) : billingPeriod === 'yearly' && product.yearly_price ? (
                          <div>
                            <div className="flex items-baseline justify-center gap-2">
                              {product.monthly_price && product.monthly_price > 0 && (
                                <span className="text-lg text-muted-foreground line-through">{product.monthly_price}</span>
                              )}
                              <span className="text-3xl font-bold">{Math.round(product.yearly_price / 12)}</span>
                              <span className="text-muted-foreground text-sm">ر.س / شهرياً</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {product.yearly_price} ر.س تدفع سنوياً
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-3xl font-bold">{product.monthly_price || product.price}</span>
                            <span className="text-muted-foreground text-sm">ر.س / شهرياً</span>
                          </div>
                        )}
                      </div>

                      <CardDescription className="text-sm mt-3">
                        {planDetails.subtitle || product.description_ar}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="flex-1 flex flex-col gap-5 pt-0">
                      {/* Features Section */}
                      <div className="border-t pt-4">
                        <p className="text-sm font-semibold mb-4">أبرز الخصائص</p>
                        <div className="space-y-3">
                          {planDetails.common.map((feature, i) => (
                            <div key={`common-${i}`} className="flex items-start gap-2.5 text-sm">
                              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </div>
                          ))}
                          {planDetails.differentiators.length > 0 && (
                            <div className="border-t border-dashed my-2" />
                          )}
                          {planDetails.differentiators.map((d, i) => (
                            <div key={`diff-${i}`} className={`flex items-start gap-2.5 text-sm ${d.included ? '' : 'text-muted-foreground'}`}>
                              {d.included ? (
                                <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5" />
                              )}
                              <span>{d.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bottom section pushed down */}
                      <div className="mt-auto space-y-4">
                        {/* CTA Button */}
                        <Button 
                          className="w-full" 
                          variant={isPopular ? "default" : isFree ? "outline" : "default"}
                          onClick={() => isEnterprise ? setShowEnterpriseDialog(true) : handleStartClinic(product.id)}
                        >
                          {clinic && !isEnterprise ? 'الذهاب إلى عيادتي' : isEnterprise ? 'تواصل معنا' : isFree ? 'ابدأ مجاناً' : 'ابدأ الآن'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl">
          <AnimatedText className="text-3xl font-bold text-center mb-12">
            الأسئلة الشائعة
          </AnimatedText>
          <AnimatedCard index={0}>
            <Accordion type="single" collapsible className="w-full">
              {startClinicFaqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-right hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedCard>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-4xl text-center">
          <AnimatedText className="text-3xl font-bold mb-8">
            مستعد لبدء عيادتك الإلكترونية؟
          </AnimatedText>
          <AnimatedSection delay={0.1}>
            <Button 
              size="lg" 
              onClick={() => {
                if (!user) {
                  localStorage.setItem('authReturnTo', '/clinic/start');
                  navigate('/auth?returnTo=%2Fclinic%2Fstart');
                } else {
                  navigate('/clinic/register');
                }
              }}
              className="text-lg px-8"
            >
              {user ? 'ابدأ الآن' : 'سجل الآن'}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Enterprise Contact Dialog */}
      <Dialog open={showEnterpriseDialog} onOpenChange={setShowEnterpriseDialog}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>طلب باقة المؤسسات</DialogTitle>
            <DialogDescription>أدخل بيانات المؤسسة وسنتواصل معك في أقرب وقت</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!user) { navigate('/auth'); return; }
            setSubmittingEnterprise(true);
            try {
              const { data: profile } = await supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle();
              const ticketMessage = `📋 طلب باقة مؤسسات\n\nاسم المؤسسة: ${enterpriseForm.orgName}\nنوع المؤسسة: ${enterpriseForm.orgType}\nعدد الموظفين: ${enterpriseForm.employeeCount}\nرقم التواصل: ${enterpriseForm.phone}\nالبريد: ${enterpriseForm.email}\n\nالرسالة:\n${enterpriseForm.message}`;
              const { error } = await supabase.from('support_tickets').insert({
                user_id: user.id,
                user_email: user.email || '',
                user_name: profile?.full_name || null,
                subject: `طلب باقة مؤسسات - ${enterpriseForm.orgName}`,
                message: ticketMessage,
              });
              if (error) throw error;
              toast.success('تم إرسال طلبك بنجاح، سنتواصل معك قريباً');
              setShowEnterpriseDialog(false);
              setEnterpriseForm({ orgName: '', orgType: '', employeeCount: '', phone: '', email: '', message: '' });
            } catch {
              toast.error('حدث خطأ أثناء إرسال الطلب');
            } finally {
              setSubmittingEnterprise(false);
            }
          }} className="space-y-4">
            <div className="space-y-2">
              <Label>اسم المؤسسة *</Label>
              <Input required value={enterpriseForm.orgName} onChange={e => setEnterpriseForm(p => ({ ...p, orgName: e.target.value }))} placeholder="مثال: مركز الحياة الصحي" />
            </div>
            <div className="space-y-2">
              <Label>نوع المؤسسة *</Label>
              <Input required value={enterpriseForm.orgType} onChange={e => setEnterpriseForm(p => ({ ...p, orgType: e.target.value }))} placeholder="مثال: مركز تغذية، عيادة صحية" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>عدد الممارسين *</Label>
                <Input required type="number" min="1" value={enterpriseForm.employeeCount} onChange={e => setEnterpriseForm(p => ({ ...p, employeeCount: e.target.value }))} placeholder="5" />
              </div>
              <div className="space-y-2">
                <Label>رقم التواصل *</Label>
                <Input required value={enterpriseForm.phone} onChange={e => setEnterpriseForm(p => ({ ...p, phone: e.target.value }))} placeholder="05xxxxxxxx" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>البريد الإلكتروني *</Label>
              <Input required type="email" value={enterpriseForm.email} onChange={e => setEnterpriseForm(p => ({ ...p, email: e.target.value }))} placeholder="info@example.com" />
            </div>
            <div className="space-y-2">
              <Label>رسالتك</Label>
              <Textarea value={enterpriseForm.message} onChange={e => setEnterpriseForm(p => ({ ...p, message: e.target.value }))} placeholder="أخبرنا عن احتياجاتك..." rows={3} />
            </div>
            <Button type="submit" className="w-full" disabled={submittingEnterprise}>
              {submittingEnterprise ? 'جاري الإرسال...' : 'إرسال الطلب'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <WhatsAppWidget phoneNumber="966533164917" />

      <Footer />
    </div>
  );
};

export default StartClinic;
